import {
  BooleanInput,
  SimpleForm,
  TextInput,
  FormDataConsumer,
  required,
  SimpleFormProps,
  SelectInput,
  Loading,
} from 'react-admin';
import {
  CampaignLineItemsSelectInput,
  CurrentLineItemDateInputGroup,
  FormDivider,
  FormHeader,
  FormSectionHeader,
  TitleInput,
} from './form-components';
import { validateUrlFormat, validateContentId } from './form-utils';
import range from 'lodash/range';
import { PreviewFMAButton } from '../fma-creatives';
import { CreativeToolbar, TargetListAutocompleteInput } from './shared';
import { useRecordContext } from 'react-admin';
import { useNativeLineItem } from './hooks/useNativeLineItem';

export interface CreativesFormProps extends Partial<SimpleFormProps> {
  type: 'Create' | 'Update';
}

export const FmaCreativeForm = ({ type, ...rest }: CreativesFormProps) => {
  const record = useRecordContext();

  // Resolve the parent line item from native data for BOTH id forms — the
  // Placements.IO API shut off at contract end, so the legacy 'line_items'
  // proxy can no longer serve imported line items. On EDIT a saved row prefers
  // its native lineItemId FK; on CREATE only placementsLineItemId is seeded
  // (native UUID or legacy numeric id, which useNativeLineItem translates via
  // externalPioId).
  const { lineItem, isLoading, error } = useNativeLineItem(record.lineItemId ?? record.placementsLineItemId);
  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR: {error.toString()}</p>;
  }
  if (!lineItem) return null;

  const campaignId = lineItem.campaignId;

  const defaultValues = {
    ...rest.defaultValues,
    startsAt: lineItem.startDate,
    endsAt: lineItem.endDate,
  };

  return (
    <SimpleForm
      {...rest}
      defaultValues={defaultValues}
      toolbar={<CreativeToolbar />}
      mode="onBlur"
      reValidateMode="onBlur"
    >
      <FormHeader>{`${type} FMA Creative, Targeting & Pacing`}</FormHeader>

      <CampaignLineItemsSelectInput campaignId={campaignId} />

      <FormSectionHeader>Creative Details</FormSectionHeader>

      <BooleanInput source="isApproved" label="Approved" defaultValue={false} />

      <PreviewFMAButton />

      <TextInput label="Flight / Segment Name" source="name" resettable fullWidth multiline validate={required()} />

      <CurrentLineItemDateInputGroup />

      <TitleInput label="FMA Title" />
      <TextInput
        label="Company / Brand Name"
        source="companyName"
        fullWidth
        resettable
        multiline
        validate={required()}
      />
      <TextInput
        variant="outlined"
        label="FMA Body HTML"
        source="bodyHtml"
        multiline
        fullWidth
        minRows={10}
        maxRows={30}
        validate={required()}
      />
      <TextInput
        label="Contentful FMA Microsurvey ID"
        source="contentfulFmaMicrosurveyId"
        fullWidth
        resettable
        validate={[validateContentId]}
      />
      <TextInput
        variant="outlined"
        label="FMA ISI HTML"
        source="isiHtml"
        multiline
        fullWidth
        minRows={10}
        maxRows={30}
      />

      <TextInput source="jobNumber" resettable sx={{ display: 'inline-flex', marginRight: '1rem' }} />
      <TextInput
        label="Touchpoint 2 URL"
        source="showMoreInfoUrl"
        fullWidth
        resettable
        validate={[validateUrlFormat]}
      />
      <FormDataConsumer>
        {({ formData }) => (
          <BooleanInput
            label={`Countdown timer on FMA ${formData.enableCountdownCloseButton ? 'ON' : 'OFF'}`}
            source="enableCountdownCloseButton"
          />
        )}
      </FormDataConsumer>
      <FormDivider />
      <FormSectionHeader>Targeting Details</FormSectionHeader>

      <TargetListAutocompleteInput source="targetListIdsList" validate={validateTargeting} />
      <FormDivider />

      <FormSectionHeader>Pacing</FormSectionHeader>
      <SelectInput
        label="Priority"
        source="priority"
        validate={[required()]}
        defaultValue={8}
        sx={{ minWidth: 250 }}
        helperText="Between 1 (Highest) and 16 (Lowest)"
        choices={range(1, 17).map(value => {
          return {
            id: value,
            name: `${value}`,
          };
        })}
      />
      <SelectInput
        label="Show number of times per week"
        source="viewsPerWeek"
        validate={[required()]}
        defaultValue={2}
        sx={{ minWidth: 250 }}
        helperText="Between 1 and 4"
        choices={range(1, 5).map(value => {
          return {
            id: value,
            name: `${value}`,
          };
        })}
      />
    </SimpleForm>
  );
};

interface FmaCreativeTargetingInputs {
  isApproved: boolean;
  targetListIdsList?: number[];
}

interface FmaCreativeTargetingErrors {
  targetListIdsList?: string;
}

const validateTargeting = (value: any, allValues: any) => {
  if (allValues.isApproved && (allValues.targetListIdsList?.length ?? 0) === 0) {
    return 'If Target List targeting is selected, you must select at least one target list';
  }
  return undefined;
};